import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as HttpStatus from 'http-status-codes';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/timer';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/scan';
import 'rxjs/add/operator/share';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/shareReplay';

import { IAccount } from '../../models/account';
import { ISiteSettings } from '../../models/settings';
import { restPath } from '../../app/constants';
import { ToastController } from 'ionic-angular';
import { LoginProvider } from '../login/login';
import { IVehicle } from '../../models/vehicle';
import status from './ad-status';
import { IStatus } from './ad-status';
import { IRestResponse } from '../../models/rest';
import { ISetting } from '../../models/setting';
import { IAdSite, IAdSiteDetails } from '../../models/site';
import { IResult, IResultData } from '../../models/result';
import { IRemark } from '../../models/remark';
import { DomProvider } from '../dom/dom';

@Injectable()
export class HexonProvider {
  public vehicles$: Observable<IVehicle[]>;
  public status: IStatus;

  private siteSettingsUrl;
  private baseUrl: string;

  constructor(
    public http: HttpClient,
    private toastCtrl: ToastController,
    private login: LoginProvider,
    private dom: DomProvider
  ) {
    this.status = status;
    this.vehicles$ = this.getVehicles$(0);

    this.baseUrl = localStorage.getItem('base_url');
    this.siteSettingsUrl = this.baseUrl + 'oct/sitesettings/{sitecode}';
  }

  public getSiteSettings$(siteCode: string) {
    const siteSettingsUrl = this.siteSettingsUrl.replace(
      '{sitecode}',
      siteCode
    );

    return this.get<IResult<ISiteSettings[]>>(siteSettingsUrl)
      .map(result => result.data[0].results)
      .share();
  }

  public setSiteSettings$(siteCode: string, data) {
    const siteSettingsUrl = this.siteSettingsUrl.replace(
      '{sitecode}',
      siteCode
    );

    return this.post<IResult<ISiteSettings[]>>(siteSettingsUrl, data).share();
  }

  public activate$(siteCode: string) {
    return this.post(`${this.baseUrl}oct/activate/${siteCode}`, '');
  }

  public deactivate$(siteCode: string, removeAds: boolean) {
    return this.post(`${this.baseUrl}oct/deactivate/${siteCode}`, {
      current_ads: removeAds ? 'remove' : 'preserve'
    });
  }

  public getAllSites$() {
    return this.get<IResult<IAdSite[]>>(
      `${this.baseUrl}oct/sites?support=true&status=available`
    ).share();
  }

  public getSite$(siteCode: string) {
    return this.get<IResult<IAdSiteDetails[]>>(
      `${this.baseUrl}oct/site/${siteCode}`
    ).share();
  }

  public getSetting$() {
    return this.get<IResultData<ISetting>>(
      `${this.baseUrl}setting/?fields=name_own_company`
    ).share();
  }

  public getVehicles$(page: number, query = '', field = '') {
    const vehicleUrl =
      `${
        this.baseUrl
      }oct/vehiclesext/?fields=identification.license_plate,history.creation_date,` +
      `general.make.name,general.model.name,general.type.name,stocknumber&sort=general.make.name&page={page}`;

    let url = vehicleUrl.replace('{page}', String(page));

    if (query) {
      if (field === 'identification.license_plate') {
        query = query.replace(/-/g, '');
      }
      url += `&filter=${field}&filtervalue=${query}`;
    }

    return this.get<IResult<IVehicle[]>>(url).map(results =>
      results.data[0].results.map(result => ({
        ...result,
        historyCreationDate: result['history.creation_date'],
        identificationLicensePlate: result['identification.license_plate'],
        generalMakeName: result['general.make.name'],
        generalModelName: result['general.model.name'],
        generalTypeName: result['general.type.name']
      }))
    );
  }

  public getAccounts$() {
    return this.get<IResult<IAccount[]>>(`${this.baseUrl}oct/accounts`)
      .map(result =>
        result.data[0].results
          .map(siteResult => ({
            ...siteResult,
            active: siteResult.permission_site === 'ok'
          }))
          .sort(
            (a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
          )
      )
      .shareReplay(1);
  }

  public getRemark() {
    return this.get<IResult<IRemark[]>>(`${this.baseUrl}oct/remark/1`);
  }

  public setRemark(data: IRemark, isNew: boolean) {
    return isNew
      ? this.post(`${this.baseUrl}oct/remark/1`, data)
      : this.put(`${this.baseUrl}oct/remark/1`, data);
  }

  /* Utility functions */

  private get<T>(url: string) {
    return this.login.data$
      .switchMap(
        () =>
          this.http
            .get<T>(url)
            .catch(this.handleError.bind(this)) as Observable<T>
      )
      .retry(1);
  }

  private post<T extends IRestResponse>(url: string, params) {
    return this.login.data$.switchMap(() =>
      this.http
        .post<T>(url, params)
        .map(d => {
          const { errors, warnings } = d.data[0];
          const data = d.data;
          if ((errors && errors.length) || (warnings && warnings.length)) {
            const error = JSON.stringify({ errors, warnings });
            throw new Error(error);
          }

          return { errors, warnings, data };
        })
        .catch(this.handleError.bind(this))
    );
  }

  private put<T extends IRestResponse>(url: string, data) {
    return this.login.data$.switchMap(() =>
      this.http
        .put<T>(url, data)
        .map(d => {
          const { errors, warnings } = d.data[0];
          if ((errors && errors.length) || (warnings && warnings.length)) {
            const error = JSON.stringify({ errors, warnings });
            throw new Error(error);
          }
          return { errors, warnings };
        })
        .catch(this.handleError.bind(this))
    );
  }

  private handleError(e) {
    if (e.status === HttpStatus.UNAUTHORIZED) {
      console.error('Authenticatiefout', e);
      // delete token
      localStorage.removeItem('token');
      this.login.authenticate();
      this.toastCtrl
        .create({
          message: 'Uw sessie is verlopen. Log opnieuw in.',
          duration: 5000,
          position: 'bottom'
        })
        .present();
    } else {
      console.error('Netwerkfout', e);
      this.toastCtrl
        .create({
          message: 'Er is een fout opgetreden. Probeer het later nog een keer.',
          duration: 5000,
          position: 'bottom'
        })
        .present();
    }
    return Observable.of({ data: [{ results: [], errors: [], warnings: [] }] });
  }
}



// WEBPACK FOOTER //
// ./src/providers/hexon/hexon.ts